import { useTikTokAdsConnectionStatus } from "@/api/tiktok-ads";
import { DisabledActionMenuItem } from "@/components/assurance/disabled-action-menu-item";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { useFeatureFlag } from "@/hooks/use-feature-flag";
import type { ActionCategory } from "@/lib/assurance/action-types";
import {
  type ActionType,
  ADS_NOT_CONNECTED_TOOLTIP,
  getActionTypeIcon,
  getLogActionLabel,
  getTakeActionLabel,
  LOG_ACTION_TYPES,
  type LogActionType,
  MANUAL_TAKE_ACTION_TYPES,
  type UiTakeActionType,
} from "@/lib/assurance/action-types";

type TakeActionOption = { type: UiTakeActionType; disabledReason?: string };

function useTakeActionTypes({ isInAdCampaign }: { isInAdCampaign?: boolean } = {}): readonly TakeActionOption[] {
  const triggerPolicyEnabled = useFeatureFlag("F-trigger-policy");
  const { data: adsConnectionStatus } = useTikTokAdsConnectionStatus();
  const adsConnected = adsConnectionStatus?.connected === true;

  const options: TakeActionOption[] = [];
  for (const type of MANUAL_TAKE_ACTION_TYPES) {
    if (type === "remove_from_ad_campaign") {
      if (!adsConnected) {
        options.push({ type, disabledReason: ADS_NOT_CONNECTED_TOOLTIP });
      } else if (isInAdCampaign ?? true) {
        // Bulk menu leaves isInAdCampaign undefined (can't know per-video); the dialog no-ops if none apply.
        options.push({ type });
      }
      continue;
    }
    options.push({ type });
  }
  if (triggerPolicyEnabled) options.push({ type: "trigger_policy" });
  return options;
}

interface ActionMenuItemsProps {
  onSelectAction: (actionType: ActionType, category: ActionCategory) => void;
}

/**
 * Renders Take Action menu items.
 */
const TakeActionItems = ({
  actions,
  onSelectAction,
}: {
  actions: readonly TakeActionOption[];
  onSelectAction: ActionMenuItemsProps["onSelectAction"];
}) => {
  return (
    <>
      {actions.map(({ type, disabledReason }) => {
        const Icon = getActionTypeIcon(type);
        const label = getTakeActionLabel(type);
        if (disabledReason) {
          return <DisabledActionMenuItem key={type} icon={Icon} label={label} tooltip={disabledReason} />;
        }
        return (
          <DropdownMenuItem
            key={type}
            onClick={() => onSelectAction(type, "take")}
            className="flex items-center gap-2 py-2"
          >
            <Icon className="w-4 h-4 text-fg-quaternary shrink-0" />
            <span className="text-secondary font-semibold">{label}</span>
          </DropdownMenuItem>
        );
      })}
    </>
  );
};

/**
 * Renders Log Action menu items.
 */
const LogActionItems = ({
  actions,
  onSelectAction,
}: {
  actions: readonly LogActionType[];
  onSelectAction: ActionMenuItemsProps["onSelectAction"];
}) => {
  return (
    <>
      {actions.map((actionType) => {
        const Icon = getActionTypeIcon(actionType);
        return (
          <DropdownMenuItem
            key={actionType}
            onClick={() => onSelectAction(actionType, "log")}
            className="flex items-center gap-2 py-2"
          >
            <Icon className="w-4 h-4 text-fg-quaternary shrink-0" />
            <span className="text-secondary font-semibold">{getLogActionLabel(actionType)}</span>
          </DropdownMenuItem>
        );
      })}
    </>
  );
};

/**
 * Take Action menu items for the bulk action dropdown.
 */
export const TakeActionMenuItems = ({ onSelectAction }: ActionMenuItemsProps) => {
  const actions = useTakeActionTypes();
  return <TakeActionItems actions={actions} onSelectAction={onSelectAction} />;
};

/**
 * Log Action menu items for the bulk action dropdown.
 */
export const LogActionMenuItems = ({ onSelectAction }: ActionMenuItemsProps) => {
  return <LogActionItems actions={LOG_ACTION_TYPES} onSelectAction={onSelectAction} />;
};
